import amapi from '@/api/amapi'

/**
 * Publishing an app at a subdomain of the customer's OWN domain, with the DNS
 * record set for them through the calls the portal already uses:
 *  - a domain in one of their hosting accounts whose cPanel zone is the one
 *    answering for it → that zone (cPanel ZoneEdit);
 *  - a domain registered with us on eNom's DNS ("DNS Hosted") → eNom host records;
 *  - anything else → we can't, so the customer points it themselves.
 * Nothing is ever replaced without the customer saying so (see `conflicts`).
 */

// cPanel zones: subdomains ("sub") live inside their parent's zone.
const ZONE_TYPES = ['main', 'addon', 'parked']
// Records that decide where a name goes (an A record can't sit beside these).
const POINTING = ['A', 'AAAA', 'CNAME', 'URL', 'FRAME']
const LABEL = /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/
// eNom keeps at most 50 host records per domain.
const ENOM_MAX_RECORDS = 50

/**
 * The domains an app can be published under: the zones in their hosting
 * accounts and the domains registered with us (active ones).
 * → [{ domain, inHosting, serviceId, username, domainId }] by name.
 *
 * @param {Array} registered the store's `domains` (servicelist rows: { id, domain, status })
 */
export async function loadOwnDomains (registered = []) {
  const res = await amapi.makecall('websites').catch(() => ({ websites: [] }))
  const by = {}
  for (const w of (res && res.websites) || []) {
    if (!ZONE_TYPES.includes(w.domaintype)) continue
    const domain = String(w.domain).toLowerCase()
    by[domain] = { domain, inHosting: true, serviceId: String(w.service_id), username: w.username, domainId: null }
  }
  for (const r of registered || []) {
    if (r.status && r.status !== 'Active') continue
    const domain = String(r.domain).toLowerCase()
    by[domain] = { ...(by[domain] || { domain, inHosting: false, serviceId: null, username: null }), domainId: String(r.id) }
  }
  return Object.values(by).sort((a, b) => a.domain.localeCompare(b.domain))
}

/**
 * Why this subdomain can't be used (null = fine). A domain in a hosting account
 * keeps its apex and www — that's the website.
 */
export function subdomainProblem (entry, sub) {
  const s = String(sub || '').toLowerCase()
  if (entry && entry.inHosting && (!s || s === 'www')) {
    return `${entry.domain} and www.${entry.domain} are your website — choose another subdomain.`
  }
  if (s && !s.split('.').every(l => LABEL.test(l))) {
    return 'The subdomain can only use letters, numbers, - and dots.'
  }
  return null
}

/** The full name: "app.example.com" (or the domain itself for no subdomain). */
export const hostnameFor = (entry, sub) => (sub ? `${String(sub).toLowerCase()}.` : '') + entry.domain

/**
 * How we can set this domain's DNS: { kind: 'cpanel' | 'enom' | 'manual', entry }.
 * cPanel only when its zone really answers for the domain (DNS::has_local_authority).
 */
export async function dnsRoute (entry) {
  if (entry.serviceId) {
    try {
      const res = await amapi.makecall('cpanel_uapi', {
        id: entry.serviceId,
        username: entry.username,
        params: { uapi_module: 'DNS', uapi_function: 'has_local_authority', domain: entry.domain, _username: entry.username },
      })
      const data = res && res.payload && res.payload.data
      const row = (Array.isArray(data) ? data : [data]).find(Boolean) || {}
      if (Number(row.local_authority) === 1) return { kind: 'cpanel', entry }
    } catch (e) { /* not answerable here — try the registration */ }
  }
  if (entry.domainId) {
    try {
      const res = await amapi.makecall('domaindetail', { domain_id: entry.domainId })
      if (res && res.domain && res.domain.dnspreset === 'hosted') return { kind: 'enom', entry }
    } catch (e) { /* e.g. not an eNom registration */ }
  }
  return { kind: 'manual', entry }
}

async function zoneEdit (entry, method, args = {}) {
  const res = await amapi.makecall('cpanel_api2', {
    id: entry.serviceId,
    username: entry.username,
    params: { api2_module: 'ZoneEdit', api2_method: method, _username: entry.username, ...args },
  })
  const data = (res && res.payload && res.payload.data) || []
  // cPanel reports a refused change inside a "successful" call.
  const refused = data.find(d => d && d.result && !Number(d.result.status))
  if (refused) throw new Error(refused.result.statusmsg || 'The DNS change was refused.')
  return data
}

/** eNom names: relative, "@" for the domain itself. */
const enomName = sub => (sub ? String(sub).toLowerCase() : '@')

/**
 * What the name points at now: [{ type, address, line? }] (empty = nothing).
 */
export async function currentRecords (route, sub) {
  const { entry } = route
  if (route.kind === 'cpanel') {
    const want = hostnameFor(entry, sub) + '.'
    return (await zoneEdit(entry, 'fetchzone_records', { domain: entry.domain }))
      .filter(r => String(r.name).toLowerCase() === want && POINTING.includes(r.type))
      .map(r => ({ type: r.type, address: r.address || r.cname || r.record, line: Number(r.line) }))
  }
  if (route.kind === 'enom') {
    const res = await amapi.makecall('domain_get_hosts', { domainid: entry.domainId })
    return ((res && res.records) || [])
      .filter(r => String(r.name).toLowerCase() === enomName(sub) && POINTING.includes(r.type))
      .map(r => ({ type: r.type, address: r.address }))
  }
  return []
}

/** The records that would be replaced (anything not already an A record to `ip`). */
export const conflictsWith = (records, ip) => records.filter(r => !(ip && r.type === 'A' && r.address === ip))

/**
 * Point the name at `ip` with an A record, replacing what it pointed at — call
 * only once the customer agreed to replace any `conflicts`.
 */
export async function pointRecord (route, sub, ip) {
  const { entry } = route
  if (route.kind === 'cpanel') {
    const existing = await currentRecords(route, sub)
    if (existing.length === 1 && existing[0].type === 'A' && existing[0].address === ip) return
    // Highest line first: removing a record moves the ones below it up.
    for (const r of existing.sort((a, b) => b.line - a.line)) {
      await zoneEdit(entry, 'remove_zone_record', { domain: entry.domain, line: r.line })
    }
    await zoneEdit(entry, 'add_zone_record', { domain: entry.domain, name: hostnameFor(entry, sub) + '.', type: 'A', address: ip, ttl: 300 })
    return
  }
  if (route.kind === 'enom') {
    // eNom replaces the whole list: keep everything else, swap in our record.
    const res = await amapi.makecall('domain_get_hosts', { domainid: entry.domainId })
    const name = enomName(sub)
    const records = ((res && res.records) || [])
      .filter(r => !(String(r.name).toLowerCase() === name && POINTING.includes(r.type)))
      .map(r => ({ name: r.name, type: r.type, address: r.address, ...(r.mxpref != null ? { mxpref: r.mxpref } : {}) }))
    records.push({ name, type: 'A', address: ip })
    if (records.length > ENOM_MAX_RECORDS) {
      throw new Error(`${entry.domain} already has the most DNS records it can (${ENOM_MAX_RECORDS}) — remove one under Domains first.`)
    }
    await amapi.makecall('domain_set_hosts', { domain_id: entry.domainId, records })
  }
}
